import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Input, Space } from 'antd';
import { useDebounce, useLocale } from '../../hooks';
/**
 * SearchInput component displays a search input. It is mainly intended to be used in SearchFilterBar.
 *
 * @component
 *
 * @param {string} searchText - The initial value of the search input.
 * @param {string} searchPlaceholder - The placeholder text for the search input.
 * @param {function} onSearchChange - A callback function that is called whenever the input value changes.
 * @param {string} customStyle - Custom styles for the search input.
 *
 * @example
 * <SearchInput
 *   searchText="Initial search text"
 *   searchPlaceholder="Search for items..."
 *   onSearchChange={(newValue) => { console.log('Search input changed:', newValue); }}
 *   customStyle={{ width: '200px', border: '1px solid #ccc' }}
 * />
 *
 * Notes:
 * - The Escape key will clear the input but will not blur it, allowing the user to type a new search immediately after clearing.
 */
export const SearchInput = ({ searchText, searchPlaceholder, onSearchChange, customStyle = {}, }) => {
    const { t } = useLocale();
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState(searchText);
    const debouncedText = useDebounce(inputValue || '', 500);
    const inputStyle = {
        // set default styles first, which can be overriden by customStyle
        border: '1px solid rgba(23, 11, 1, 0.5)',
        // override default styles and add new styles with customStyle
        ...customStyle,
        // these can't be overriden
        width: isFocused ? '35ch' : '15ch',
        transition: 'width 0.3s ease-in-out',
    };
    useEffect(() => {
        if (onSearchChange) {
            onSearchChange(debouncedText);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedText]);
    const handleChange = (e) => {
        setInputValue(e.target.value);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setInputValue('');
            if (onSearchChange) {
                onSearchChange('');
            }
            // NOTE: I think I prefer not to blur, so you can press Esc and then type in a new search
            // blur the input (unset focus)
            // if (inputRef.current) {
            //   inputRef.current.blur();
            // }
        }
    };
    return (_jsx(Space.Compact, { size: 'middle', children: _jsx(Input, { ref: inputRef, value: inputValue, prefix: _jsx(SearchOutlined, { style: { color: 'rgba(0, 0, 0, 0.25)' } }), placeholder: searchPlaceholder || t('Search'), allowClear: true, style: inputStyle, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), onChange: handleChange, onKeyDown: handleKeyDown }) }));
};
